import type { User, UserLogin } from '@/shared/api/types';

import { storageKey } from '@/shared/lib/storage-key';
import { ConfigService } from '@/shared/services/config.service';
import http, { authHttp } from '@/shared/services/http.service';
import { clearToken, getToken, setToken } from '@/shared/services/token.storage';

import type {
  AuthAdapter,
  AuthSession,
  KeyValueStorage,
  LoginResult,
  MfaChallenge,
  OAuthCallbackResult,
} from './AuthAdapter';

/**
 * The base64-JSON payload the backend hands back on the Google OAuth redirect.
 *
 * Declared here rather than generated, and that is correct: it never crosses
 * the wire as a response body, it rides in a query string, so it has no
 * OpenAPI schema to generate from. Mirror of `Auth0Provider` in
 * `backend/src/core/auth/interface/auth0-provider.ts`.
 *
 * When MFA is on and the device carries no valid trust, the backend returns
 * the challenge fields *instead of* `token`, and `/auth/mfa-verify` mints the
 * real session afterwards.
 */
interface Auth0RedirectPayload {
  operation: 'signup' | 'signing';
  /** The login page refuses anything that is not a plain `user`. */
  typeUser?: 'user' | 'internal_user';
  user?: Partial<User>;
  token?: string;
  msg?: string;
  error?: unknown;
  mfa_required?: boolean;
  mfa_challenge_token?: string;
  mfa_channel?: 'email' | 'sms';
  mfa_target?: string;
}

interface MfaChallengeResponse extends MfaChallenge {
  mfa_required: true;
}

interface VerifyMfaResponse extends UserLogin {
  mfa_trust_id?: string;
}

const getBasicAuth = (username: string, password: string) => {
  const credentials = `${username}:${password}`;
  const encodingUtf8 = new TextEncoder().encode(credentials);
  const base64String = btoa(String.fromCharCode(...encodingUtf8));
  return 'Basic ' + base64String;
};

const decodeJwtPayload = (token: string): { jti?: string } | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded)) as { jti?: string };
  } catch {
    return null;
  }
};

const createLocalStorageValue = (key: string): KeyValueStorage => ({
  get: () => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable (private mode): the user is simply re-prompted.
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(key);
    } catch {
      // noop
    }
  },
});

/**
 * Auth contract of the Tenko backend (`/auth/*`, `/auth0/*`): Basic-auth login
 * with an optional MFA challenge, OTP verification with a 24h device trust,
 * one-time token login, Google OAuth round-trip through the backend, and
 * session revocation.
 */
export function createTenkoAuthAdapter(): AuthAdapter {
  const config = ConfigService.getInstance();
  // Same key value Tenko used before the split (`tenko.mfa_trust_id`).
  const mfaTrust = createLocalStorageValue(storageKey('mfa_trust_id'));

  return {
    // Which *backend* this adapter speaks to, not which dashboard is running:
    // Monokoro reuses the Tenko API. The platform is `ConfigService.appId`.
    backendId: 'tenko',
    tokens: { get: getToken, set: setToken, clear: clearToken },
    mfaTrust,

    async login({ email, password }): Promise<LoginResult> {
      // `authHttp`, not `http`: a wrong password answers 401 and must not run
      // the shared client's "session expired" handler. It still carries
      // `x-app-id`, which is how the backend knows which dashboard this login
      // belongs to — the same address can have an account on each.
      const trustId = mfaTrust.get();
      const response = await authHttp.post<
        MfaChallengeResponse | UserLogin
      >(
        '/auth/login/user',
        {},
        {
          headers: {
            Authorization: getBasicAuth(email, password),
            'Content-Type': 'application/json',
            ...(trustId ? { 'X-MFA-Trust-Id': trustId } : {}),
          },
        },
      );
      const data = response.data;
      if ('mfa_required' in data && data.mfa_required) {
        return {
          kind: 'mfa',
          challenge: {
            challenge_token: data.challenge_token,
            channel: data.channel,
            target: data.target,
          },
        };
      }
      const session = data as UserLogin;
      return { kind: 'success', session: { user: session.user, token: session.token } };
    },

    async verifyMfa({ challenge_token, otp_code }): Promise<AuthSession> {
      const response = await http.post<VerifyMfaResponse>('/auth/mfa-verify', {
        challenge_token,
        otp_code,
      });
      // Persist the trust id so the next 24h of logins on this device skip MFA.
      if (response.data.mfa_trust_id) {
        mfaTrust.set(response.data.mfa_trust_id);
      }
      return { user: response.data.user, token: response.data.token };
    },

    async tokenLogin(tokenId): Promise<AuthSession> {
      const response = await http.get<UserLogin>(
        `/auth/user/access/token-id/${tokenId}`,
      );
      return { user: response.data.user, token: response.data.token };
    },

    async logout(token): Promise<void> {
      // Runs after the local session is already cleared, so the token travels
      // explicitly: the shared client would attach nothing and its 401 handler
      // must not fire during a logout. Tokens without a `jti` (issued before
      // session tracking) have nothing to revoke.
      const jti = token ? decodeJwtPayload(token)?.jti : undefined;
      if (!jti) return;
      try {
        await authHttp.delete(`/user/me/sessions/${jti}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // best-effort revoke
      }
    },

    google: {
      buildLoginUrl() {
        // The MFA trust id rides along in `state` so the backend can skip the
        // challenge when this device already verified MFA in the trust window.
        const trustId = mfaTrust.get();
        const state = btoa(
          JSON.stringify({
            url_redirect: config.getOrThrow('loginPageUrl'),
            // Google redirects back to the backend with no `x-app-id` and no
            // usable `Origin`, so the state is the only place the platform can
            // travel. Without it the callback would sign the user into the
            // account this Google address has on another dashboard.
            platform: config.getOrThrow('appId'),
            ...(trustId ? { mfa_trust_id: trustId } : {}),
          }),
        );
        return `${config.getOrThrow('apiUrl')}/auth0/login/google?state=${state}`;
      },

      parseCallback(stateParam): OAuthCallbackResult {
        let payload: Auth0RedirectPayload;
        try {
          payload = JSON.parse(atob(stateParam));
        } catch {
          // Not base64 JSON: nothing the login page can act on.
          return { kind: 'ignored' };
        }
        if (payload.error) {
          return { kind: 'forbidden' };
        }
        if (
          payload.mfa_required &&
          payload.mfa_challenge_token &&
          payload.mfa_channel &&
          payload.mfa_target
        ) {
          return {
            kind: 'mfa',
            challenge: {
              challenge_token: payload.mfa_challenge_token,
              channel: payload.mfa_channel,
              target: payload.mfa_target,
            },
          };
        }
        if (payload.operation === 'signup' && payload.user && payload.token) {
          return {
            kind: 'success',
            session: { user: payload.user, token: payload.token },
          };
        }
        // Staff sign in with Google too; the backend keys the lookup by
        // (email, platform), so they only ever reach their own dashboard.
        const knownType =
          payload.typeUser === 'user' || payload.typeUser === 'internal_user';
        if (payload.typeUser && !knownType) {
          return { kind: 'forbidden' };
        }
        if (
          payload.operation === 'signing' &&
          knownType &&
          payload.user &&
          payload.token
        ) {
          return {
            kind: 'success',
            session: { user: payload.user, token: payload.token },
          };
        }
        return { kind: 'ignored' };
      },
    },
  };
}
